// components/common/Layout.jsx - HOTWIFI v4.3 - Fixed Admin Role Check

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  Chip,
  Button,
  useMediaQuery,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Extension as ModulesIcon,
  Hotel as RoomsIcon,
  People as UsersIcon,
  ConfirmationNumber as CouponIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Article as LogsIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as TokenIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  PrivacyTip as PrivacyTipIcon,
} from '@mui/icons-material';

import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import usePagePermissions from '../../hooks/usePagePermissions';
import { logout, setSelectedBranch, setSelectedTenant, updateAvailableBranches } from '../../store/slices/authSlice';
import { authAPI, tenantsAPI, branchesAPI } from '../../services/api';
import { dynamicPagesAPI } from '../../services/dynamicPagesApi';
import BrandLogo from './BrandLogo';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const DRAWER_WIDTH = 210;

export default function Layout({ toggleTheme, themeMode, children }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isCompactTopBar = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [branchMenuAnchor, setBranchMenuAnchor] = useState(null);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const { user, selectedBranch, availableBranches, branchSelectionRequired, selectedTenant } = useAppSelector(
    (state) => state.auth
  );
  const { connected: socketConnected } = useAppSelector((state) => state.socket || { connected: false });
  const { canViewLogs } = usePermissions();
  const { canAccessPage } = usePagePermissions();

  //  FIXED: Memoize helper functions to prevent infinite effect loops
  const isAdmin = React.useCallback(() => {
    if (user?.isTokenUser) return false;
    const userRole = user?.role?.toLowerCase().replace(/[-_]/g, ''); // Normalize role
    return ['superadmin', 'admin'].includes(userRole);
  }, [user]);

  const isSuperAdmin = React.useCallback(() => {
    if (user?.isTokenUser) return false;
    const userRole = user?.role?.toLowerCase().replace(/[-_]/g, '');
    return userRole === 'superadmin';
  }, [user]);

  const { data: tenantsResponse } = useQuery({
    queryKey: ['tenants'],
    queryFn: tenantsAPI.list,
    enabled: isSuperAdmin(),
    staleTime: 5 * 60 * 1000,  // Tenant list is stable — don't refetch on every nav
    cacheTime: 10 * 60 * 1000,
  });

  const tenants = tenantsResponse?.data || [];
  const selectedTenantId = selectedTenant?._id || selectedTenant?.id || null;

  const { data: branchesResponse } = useQuery({
    queryKey: ['branches', selectedTenantId],
    queryFn: () => branchesAPI.getAll(),
    enabled: isSuperAdmin() ? !!selectedTenantId : isAdmin(),
    staleTime: 5 * 60 * 1000,  // Branches stable — don't refetch unless tenant changes
    cacheTime: 10 * 60 * 1000,
  });

  const tenantBranches = branchesResponse?.data?.data || branchesResponse?.data || [];
  const previousTenantIdRef = useRef(null);

  // Auto-select tenant if only one exists or validate selected tenant
  useEffect(() => {
    if (!isSuperAdmin() || tenants.length === 0) return;

    // If only one tenant exists, always select it
    if (tenants.length === 1) {
      const singleTenant = tenants[0];
      const singleTenantId = singleTenant._id || singleTenant.id;
      const currentTenantId = selectedTenant?._id || selectedTenant?.id;
      
      if (currentTenantId !== singleTenantId) {
        dispatch(setSelectedTenant(singleTenant));
      }
      return;
    }

    // Validate that the selected tenant still exists in the list
    if (selectedTenant) {
      const currentTenantId = selectedTenant._id || selectedTenant.id;
      const tenantStillExists = tenants.some(t => 
        (t._id || t.id) === currentTenantId
      );
      
      if (!tenantStillExists) {
        // Selected tenant no longer exists, clear it
        dispatch(setSelectedTenant(null));
        localStorage.removeItem('selectedTenant');
      }
    }
  }, [tenants, selectedTenant, dispatch, isSuperAdmin]);

  useEffect(() => {
    if (!isAdmin()) return;

    // Only update branches when we have actual data from the query
    if (!branchesResponse) {
      return;
    }

    const branchesFromQuery = Array.isArray(branchesResponse) 
      ? branchesResponse 
      : (branchesResponse?.data?.data || branchesResponse?.data || branchesResponse?.branches || []);
    
    // Check if branches changed before dispatching to avoid infinite loops
    const currentBranchesStr = JSON.stringify(availableBranches);
    const newBranchesStr = JSON.stringify(branchesFromQuery);
    
    if (currentBranchesStr !== newBranchesStr) {
      console.log(' Layout: Branches updated from query, dispatching to Redux');
      dispatch(updateAvailableBranches(branchesFromQuery));
      
      if (isSuperAdmin()) {
        const tenantChanged = previousTenantIdRef.current !== selectedTenantId;
        if (tenantChanged) {
          previousTenantIdRef.current = selectedTenantId;
          dispatch(setSelectedBranch(branchesFromQuery[0] || null));
          return;
        }
      }

      if (!selectedBranch && branchesFromQuery.length > 0) {
        dispatch(setSelectedBranch(branchesFromQuery[0]));
      }
    }
  }, [selectedTenantId, branchesResponse, selectedBranch, availableBranches, dispatch, isSuperAdmin, isAdmin]);

  // Menu items configuration
  const branchId = selectedBranch?._id || selectedBranch?.id;

  // Fetch active dynamic pages for the current branch
  const { data: dynamicPagesData } = useQuery({
    queryKey: ['dynamicPages', branchId],
    queryFn:  () => dynamicPagesAPI.getPages(branchId, { activeOnly: true }),
    enabled:  !!branchId,
    staleTime: 60_000,
  });

  const dynamicMenuItems = React.useMemo(() => {
    const rawPages = dynamicPagesData?.data?.pages || dynamicPagesData?.pages || dynamicPagesData?.data || [];
    return rawPages.map((p) => ({
      text:     p.pageName,
      icon:     RoomsIcon,   // fallback icon — can be enhanced later with icon field
      path:     `/${p.pageKey}`,
      pageKeyDynamic: p.pageKey,
    }));
  }, [dynamicPagesData]);

  const menuItems = [
    { text: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
    { text: 'Rooms', icon: RoomsIcon, path: '/rooms' },
    ...dynamicMenuItems,
    { text: 'Modules', icon: ModulesIcon, path: '/devices' },
    { text: 'Coupons', icon: CouponIcon, path: '/vouchers' },
    { text: 'Logs', icon: LogsIcon, path: '/logs' },

    { text: 'Users', icon: UsersIcon, path: '/users', adminOnly: true },
    { text: 'Branches', icon: BusinessIcon, path: '/branches', adminOnly: true },
    { text: 'Settings', icon: SettingsIcon, path: '/settings', adminOnly: true },
    { text: 'Admin Console', icon: AdminPanelSettingsIcon, path: '/super-admin-console', superAdminOnly: true },
    { text: 'Access Token', icon: TokenIcon, path: '/access-tokens', hidden: true },
  ];

  const permissionPageMap = {
    '/dashboard': 'dashboard',
    '/rooms': 'rooms',
    '/devices': 'devices',
    '/vouchers': 'vouchers',
    '/logs': 'logs',

    '/users': 'users',
    '/branches': 'branches',
    '/settings': 'settings',
  };

  const visibleMenuItems = menuItems
    .filter((item) => !item.hidden)
    .filter((item) => !item.adminOnly || isAdmin())
    .filter((item) => !item.superAdminOnly || isSuperAdmin())
    .filter((item) => {
      if (isAdmin()) return true;

      const pageName = permissionPageMap[item.path];

      // Always allow dashboard entry
      if (pageName === 'dashboard') return true;

      // If no pagePermissions configured, keep items visible
      const hasPagePermissions = user?.pagePermissions && Object.keys(user.pagePermissions).length > 0;
      if (!hasPagePermissions) return true;

      if (!pageName) return true;
      return canAccessPage(pageName);
    })
    .filter((item) => item.text !== 'Logs' || canViewLogs);

  const pageTitle =
    menuItems.find((item) => item.path === location.pathname)?.text ||
    (location.pathname === '/support' ? 'Support' : 'Dashboard');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleBranchMenuClick = (event) => {
    setBranchMenuAnchor(event.currentTarget);
  };

  const handleBranchMenuClose = () => {
    setBranchMenuAnchor(null);
  };

  const handleBranchSelect = (branch) => {
    dispatch(setSelectedBranch(branch));
    toast.success(`Switched to ${branch.name}`);
    handleBranchMenuClose();
  };

  const handleTenantMenuClick = (event) => {
    setTenantMenuAnchor(event.currentTarget);
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchor(null);
  };

  const handleTenantSelect = (tenant) => {
    // Clear selected branch immediately when switching tenants
    localStorage.removeItem('selectedBranch');
    dispatch(setSelectedTenant(tenant));
    dispatch(setSelectedBranch(null));
    toast.success(`Switched to ${tenant.name}`);
    handleTenantMenuClose();
    navigate('/dashboard');
  };

  const handleLogout = () => {
    // Clear all React Query cache on logout
    queryClient.clear();
    dispatch(logout());
    navigate('/login');
    toast.success('Logged out successfully');
    handleUserMenuClose();
  };

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    handleUserMenuClose();
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm({
      ...passwordForm,
      [field]: event.target.value
    });
  };

  const togglePasswordVisibility = (field) => () => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSubmitPasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (!passwordForm.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    setPasswordLoading(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      handlePasswordDialogClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const drawer = (
    <Box>
      {/* Brand Header */}
      <Box
        sx={{
          px: 1.25,
          py: 1.25,
          background:
            'linear-gradient(138deg, #0f4c8a 0%, #1d6fd1 48%, #2ea2d8 100%)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 0.25,
            px: 0.5,
            py: 0.35,
            borderRadius: 1.5,
          }}
        >
          <BrandLogo
            maxWidth={78}
            imageSx={{ display: 'block' }}
          />
        </Box>


      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ px: 1.5, py: 2 }}>
        {visibleMenuItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                mb: 0.5,
                borderRadius: 1.5,
                border: '1px solid transparent',
                transition: 'all 0.22s ease',
                '&.Mui-selected': {
                  bgcolor: theme.alpha(theme.palette.primary.main, 0.17),
                  borderColor: theme.alpha(theme.palette.primary.main, 0.2),
                  boxShadow: '0 10px 22px rgba(30, 84, 165, 0.15)',
                  '&:hover': {
                    bgcolor: theme.alpha(theme.palette.primary.main, 0.22),
                  },
                },
                '&:hover': {
                  transform: 'translateX(2px)',
                  borderColor: theme.alpha(theme.palette.primary.main, 0.16),
                  bgcolor: theme.alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <item.icon />
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <CssBaseline />
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 60 },
            px: { xs: 1, sm: 1.5, md: 2 },
            gap: 0.5,
          }}
        >
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Page Title */}
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              maxWidth: { xs: '42vw', sm: '46vw', md: isTablet ? '26vw' : 'none' },
              fontSize: { xs: '1rem', sm: '1.05rem', md: '1.15rem' },
              fontWeight: 600,
            }}
          >
            {pageTitle}
          </Typography>

          {/* Tenant Dropdown Button (Super Admin) - only show if multiple tenants */}
          {isSuperAdmin() && tenants.length > 1 && (
            isCompactTopBar ? (
              <Tooltip title={selectedTenant?.name || 'Select Tenant'}>
                <IconButton color="inherit" onClick={handleTenantMenuClick} sx={{ mr: 0.25 }}>
                  <BusinessIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="inherit"
                onClick={handleTenantMenuClick}
                endIcon={<ArrowDownIcon />}
                startIcon={<BusinessIcon />}
                sx={{
                  mr: 1,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  maxWidth: 200,
                  '& .MuiButton-endIcon': { ml: 0.25 },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedTenant?.name || 'Select Tenant'}
                </Box>
              </Button>
            )
          )}

          {/* Tenant Dropdown Menu */}
          <Menu
            anchorEl={tenantMenuAnchor}
            open={Boolean(tenantMenuAnchor)}
            onClose={handleTenantMenuClose}
          >
            {tenants.map((tenant) => (
              <MenuItem
                key={tenant.id}
                onClick={() => handleTenantSelect(tenant)}
                selected={(selectedTenant?._id || selectedTenant?.id) === tenant.id}
              >
                {tenant.name}
              </MenuItem>
            ))}
          </Menu>

          {/* Branch Dropdown Button */}
          {(isAdmin() || (branchSelectionRequired && availableBranches.length > 0)) && (
            isCompactTopBar ? (
              <Tooltip title={selectedBranch?.name || 'Select Branch'}>
                <IconButton color="inherit" onClick={handleBranchMenuClick} sx={{ mr: 0.25 }}>
                  <BusinessIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="inherit"
                onClick={handleBranchMenuClick}
                endIcon={<ArrowDownIcon />}
                startIcon={<BusinessIcon />}
                sx={{
                  mr: 1,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  maxWidth: 220,
                  '& .MuiButton-endIcon': { ml: 0.25 },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedBranch?.name || 'Select Branch'}
                </Box>
              </Button>
            )
          )}

          {/* Branch Dropdown Menu */}
          <Menu
            anchorEl={branchMenuAnchor}
            open={Boolean(branchMenuAnchor)}
            onClose={handleBranchMenuClose}
          >
            {Array.isArray(availableBranches) && availableBranches.length > 0 ? (
              availableBranches.map((branch) => (
                <MenuItem
                  key={branch.id || branch._id}
                  onClick={() => handleBranchSelect(branch)}
                  selected={(selectedBranch?.id || selectedBranch?._id) === (branch.id || branch._id)}
                >
                  {branch.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No branches available</MenuItem>
            )}
          </Menu>

          {/* Theme Toggle Button */}
          <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 0.25 }}>
            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* User Menu */}
          <IconButton color="inherit" onClick={handleUserMenuClick} sx={{ p: { xs: 0.5, sm: 0.75 } }}>
            <Avatar sx={{ width: { xs: 30, sm: 32 }, height: { xs: 30, sm: 32 }, bgcolor: user?.isTokenUser ? 'warning.main' : 'secondary.main' }}>
              {user?.isTokenUser ? (
                <TokenIcon sx={{ fontSize: 18 }} />
              ) : (
                user?.username?.charAt(0).toUpperCase() || 'U'
              )}
            </Avatar>
          </IconButton>

          {/* User Dropdown Menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
          >
            <MenuItem disabled>
              <Stack>
                <Typography variant="body2" sx={{
                  fontWeight: "bold"
                }}>
                  {user?.isTokenUser ? user.displayName || user.tokenUsername : user?.username || 'User'}
                </Typography>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {user?.isTokenUser ? (
                    <Stack direction="row" spacing={0.5} sx={{
                      alignItems: "center"
                    }}>
                      <TokenIcon sx={{ fontSize: 12 }} />
                      <span>{user.tokenUsername || 'TOKEN USER'}</span>
                    </Stack>
                  ) : (
                    user?.role?.replace('_', ' ').toUpperCase() || 'USER'
                  )}
                </Typography>
                {user?.isTokenUser && user?.expiresAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "warning.main",
                      mt: 0.5
                    }}>
                    Expires: {new Date(user.expiresAt).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </MenuItem>
            <Divider />
            {/* Show Access Tokens only for non-token users */}
            {!user?.isTokenUser && (
              <MenuItem onClick={() => {
                setUserMenuAnchor(null);
                navigate('/access-tokens');
              }}>
                <ListItemIcon>
                  <TokenIcon fontSize="small" />
                </ListItemIcon>
                Access Tokens
              </MenuItem>
            )}

            {/* Show Change Password only for non-token users */}
            {!user?.isTokenUser && (
              <MenuItem onClick={handlePasswordDialogOpen}>
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                Change Password
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer - Temporary overlay */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRadius: 0,
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer - Permanent fixed sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRadius: 0,
              bgcolor: 'background.paper',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          overflowX: 'hidden',
          minHeight: '100vh',
          position: 'relative',
          background: isDarkMode
            ? 'radial-gradient(circle at 16% 14%, rgba(22, 22, 22, 0.22) 0%, rgba(22, 22, 22, 0) 35%), radial-gradient(circle at 86% 10%, rgba(12, 12, 12, 0.2) 0%, rgba(12, 12, 12, 0) 32%), linear-gradient(160deg, rgba(0, 0, 0, 0.94) 0%, rgba(4, 4, 4, 0.92) 44%, rgba(0, 0, 0, 0.96) 100%)'
            : 'radial-gradient(circle at 16% 14%, rgba(96, 165, 250, 0.16) 0%, rgba(96, 165, 250, 0) 35%), radial-gradient(circle at 86% 10%, rgba(16, 185, 129, 0.14) 0%, rgba(16, 185, 129, 0) 32%), linear-gradient(160deg, rgba(249, 252, 255, 0.78) 0%, rgba(239, 246, 255, 0.72) 44%, rgba(250, 252, 255, 0.82) 100%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: isDarkMode
              ? 'linear-gradient(115deg, rgba(34, 34, 34, 0.24) 0%, rgba(34, 34, 34, 0) 45%)'
              : 'linear-gradient(115deg, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0) 45%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
        {/* Toolbar spacer to push content below AppBar */}
        <Toolbar />

        {/* Page content */}
        {children || <Outlet />}
      </Box>
      {/* Change Password Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              fullWidth
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange('currentPassword')}
              disabled={passwordLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility('current')}
                      edge="end"
                      disabled={passwordLoading}
                    >
                      {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              fullWidth
              value={passwordForm.newPassword}
              onChange={handlePasswordChange('newPassword')}
              disabled={passwordLoading}
              helperText="Minimum 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility('new')}
                      edge="end"
                      disabled={passwordLoading}
                    >
                      {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              fullWidth
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange('confirmPassword')}
              disabled={passwordLoading}
              error={passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword}
              helperText={
                passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility('confirm')}
                      edge="end"
                      disabled={passwordLoading}
                    >
                      {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} disabled={passwordLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitPasswordChange} 
            variant="contained" 
            disabled={
              passwordLoading || 
              !passwordForm.currentPassword || 
              !passwordForm.newPassword || 
              !passwordForm.confirmPassword ||
              passwordForm.newPassword !== passwordForm.confirmPassword
            }
            startIcon={passwordLoading && <CircularProgress size={16} />}
          >
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

console.log(' LAYOUT.JSX v4.3 - Fixed admin role check (supports super_admin, superadmin, admin)');
